import { Heading2, Heading3 } from '@casavo/base-ui'
import { Body, H3, H4 } from '@casavo/habitat'
import cn from 'classnames'
import useTranslation from 'next-translate/useTranslation'
import React, { Dispatch, SetStateAction } from 'react'

import { Floor } from '@/components/Floor'
import { CasavoAdvantage } from '@/containers/ListingDetail/CasavoAdvantage'
import Characteristics from '@/containers/ListingDetail/Characteristics'
import { responsiveContainerDetail } from '@/containers/ListingDetail/styles.css'
import { useCities } from '@/context/Cities'
import { useDevice } from '@/context/Device'
import { useCurrentTenant } from '@/context/TenantProvider'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { TENANTS } from '@/lib/tenants/constants'
import { Tenants } from '@/lib/tenants/types'
import { flexBetween, pointShapeDivider } from '@/shared-styles/styles.css'
import { Potential_Property } from '@/types/cate-graphql-generated-types'
import { CasavoInventory } from '@/types/listing'
import { formatListingPricePerSquareMeter } from '@/utils/format-listing-pricePerSquareMeter'
import { formatListingSellingPrice } from '@/utils/format-listing-sellingPrice'
import { roundPrice } from '@/utils/utils-potential-properties'
import { InventoryBannerSmall } from 'components/InventoryBannerSmall'

import { getCharacteristics, getRooms } from '../../data'
import { priceAndCommercialAreaWrapper } from '../../styles.css'

import { FloatingBanner } from './FloatingBanner'
import { PriorityPriceInfo } from './PriorityPriceInfo/PriorityPriceInfo'

type Props = {
  advantages: ReadonlyArray<CasavoAdvantage>
  disableVisitCTA: boolean
  handleBannerCtaClick: VoidFunction
  handleRequestVisitCtaBuyerInfo: VoidFunction
  hasBuyerInfo: boolean
  isPriorityLocked: boolean
  priority: Potential_Property
  setShowToastError: Dispatch<SetStateAction<boolean>>
  showAlreadyBookedBanner: boolean
  thankYouPageUrl: string
}

function getInventoryBannerKey(tenant: Tenants, isPriorityLocked: boolean): string {
  switch (tenant) {
    case TENANTS.ucsc:
      return 'unicredit:PriorityDetail_TeasingBanner_Description_v2'
    case TENANTS.casavo:
      return isPriorityLocked
        ? 'priority:PriorityDetail_TeasingBanner_NotLogged_Description'
        : 'priority:Priority_DetailPage_Banner_Description'
  }
}

const titleTenantTypography: Record<Tenants, typeof Heading2 | typeof Heading3> = {
  [TENANTS.casavo]: Heading2,
  [TENANTS.ucsc]: Heading3,
}

export const PriorityDetailInfo: React.FC<Props> = ({
  advantages,
  disableVisitCTA,
  handleBannerCtaClick,
  handleRequestVisitCtaBuyerInfo,
  hasBuyerInfo,
  isPriorityLocked,
  priority,
  setShowToastError,
  showAlreadyBookedBanner,
  thankYouPageUrl,
}) => {
  const device = useDevice()
  const cities = useCities()
  const { lang, t } = useTranslation()
  const tenant = useCurrentTenant()

  const { askedPrice, buildingFloors, commercialArea, entranceFloor, estimatedListingPrice, transactionId } = priority
  const roundedAskedPrice = roundPrice(priority.askedPrice || priority.valuation)
  const roundedEstimatedListingPrice = estimatedListingPrice && roundPrice(estimatedListingPrice)
  const showPriceInfo = priority.status === 'priority' && estimatedListingPrice
  const TitleTypography = titleTenantTypography[tenant]

  return (
    <div className={responsiveContainerDetail}>
      <div>
        <div>
          <InventoryBannerSmall
            color="brandedBackground"
            i18nKeyLabel={getInventoryBannerKey(tenant, isPriorityLocked)}
            inventory={CasavoInventory.Priority}
            tenant={tenant}
            onClick={handleBannerCtaClick}
          />

          <div
            className={sprinkles({
              alignItems: { desktop: 'center' },
              display: { desktop: 'flex' },
              paddingBottom: { mobile: 'm' },
            })}
          >
            <div
              className={sprinkles({
                paddingBottom: { desktop: 0, mobile: 's' },
                textTransform: 'uppercase',
              })}
            >
              <Body noMargin color="root.neutral.600" size="s">
                {cities.list.find((city) => city.slug === priority.city)?.translatedCityName}
              </Body>
            </div>

            {priority?.zone && (
              <>
                {device === 'desktop' && <div className={pointShapeDivider} />}
                <Body noMargin color="root.neutral.500" size="s">
                  {priority?.zone.label}
                </Body>
              </>
            )}
          </div>

          <TitleTypography
            as="h1"
            className={sprinkles({
              marginBottom: 'l',
            })}
          >
            {priority.title}
          </TitleTypography>
        </div>

        {showPriceInfo ? (
          <PriorityPriceInfo
            disableVisitCta={disableVisitCTA}
            handleRequestVisitCtaBuyerInfo={handleRequestVisitCtaBuyerInfo}
            hasBuyerInfo={hasBuyerInfo}
            price={{
              askedPrice: roundedAskedPrice,
              estimatedListingPrice: roundedEstimatedListingPrice,
            }}
            priority={{
              buildingFloors,
              commercialArea,
              entranceFloor,
              transactionId,
            }}
            setShowToastError={setShowToastError}
            showAlreadyBookedBanner={showAlreadyBookedBanner}
            thankYouPageUrl={thankYouPageUrl}
          />
        ) : (
          <div className={priceAndCommercialAreaWrapper}>
            <div className={cn(flexBetween, sprinkles({ columnGap: 'm' }))}>
              {Boolean(priority.commercialArea) && <H4 noMargin>{priority.commercialArea} m&sup2;</H4>}
              <H4 noMargin>
                <Floor
                  buildingFloors={priority.buildingFloors}
                  entireBuilding={false}
                  entranceFloor={priority.entranceFloor}
                  floorPosition={[]}
                />
              </H4>
            </div>

            {askedPrice && (
              <div className={sprinkles({ alignSelf: { mobile: 'flex-start' }, textAlign: 'right' })}>
                <div className={sprinkles({ paddingBottom: { mobile: 'xs' } })}>
                  <H3 noMargin>{formatListingSellingPrice(false, roundedAskedPrice, t, lang)}</H3>
                </div>
                {Boolean(priority.commercialArea) && (
                  <Body noMargin color="root.neutral.500" size="xs">
                    {formatListingPricePerSquareMeter(askedPrice, commercialArea, lang)}
                  </Body>
                )}
              </div>
            )}
          </div>
        )}

        <Characteristics
          characteristics={getCharacteristics(priority, t)}
          className={sprinkles({ marginTop: 'xl' })}
          rooms={getRooms(priority.rooms)}
        />
      </div>

      {device === 'desktop' && (
        <div>
          <FloatingBanner
            advantages={advantages}
            disableVisitCTA={disableVisitCTA}
            handleRequestVisitCtaBuyerInfo={handleRequestVisitCtaBuyerInfo}
            hasBuyerInfo={hasBuyerInfo}
            setShowToastError={setShowToastError}
            showAlreadyBookedBanner={showAlreadyBookedBanner}
            thankYouPageUrl={thankYouPageUrl}
            transactionId={priority.transactionId}
          />
        </div>
      )}
    </div>
  )
}
